import React from 'react';
import { HEADER_HEIGHT } from '@/shared/constants/layout';

/**
 * PageToolbar — toolbar única do Design System (contrato 3.3 / R0.3).
 *
 * Substitui o PageHeader antigo (ícone + título + descrição + métricas) nas telas
 * redesenhadas. UMA linha de HEADER_HEIGHT (56px), 3 zonas:
 *  - left:   breadcrumb OU ContextSelector (+ count pill)
 *  - center: busca SEMPRE centralizada (opcional)
 *  - right:  ações (view-toggle, Filtros, segmented, ÚNICO primário roxo)
 *
 * Full-width (o cap de largura é do CONTEÚDO da página, nunca do toolbar).
 * Mobile-first: as zonas colapsam por conta dos filhos (hidden sm:inline etc.);
 * o toolbar em si mantém 1 linha com overflow controlado pelos filhos.
 *
 * @example
 * <PageToolbar
 *   left={<Breadcrumb .../>}
 *   center={<SearchBox .../>}
 *   right={<><ViewToggle/><FiltersButton/><PrimaryButton/></>}
 * />
 */
export interface PageToolbarProps {
  left: React.ReactNode;
  center?: React.ReactNode;
  right?: React.ReactNode;
  /** Linha extra (chips de filtro / bulk bar) renderizada ABAIXO do toolbar, mesma casca. */
  below?: React.ReactNode;
  /**
   * Linha MOBILE própria (`.m-sub` do mockup) — quando fornecida, o grid desktop
   * fica `hidden sm:grid` e esta linha renderiza só em < sm. Nunca "desktop
   * espremido": a anatomia mobile é desenhada por tela.
   */
  mobile?: React.ReactNode;
  /**
   * Breakpoint em que a linha `mobile` troca para o grid desktop. Default `'sm'`
   * (640px — nenhuma das ~26 páginas existentes que usam `mobile` muda).
   * `900` mantém a anatomia mobile até 899px, para telas cujo shell/corpo
   * também só vira desktop a partir de `min-[900px]:` (ex.: SellerDashboardPage)
   * — sem isso, 640–899px ficava numa faixa "sem abas" (grid desktop do
   * toolbar já visível, mas corpo ainda mobile). Classes estáticas: Tailwind
   * não aceita classe montada em runtime, por isso os dois ramos abaixo usam
   * os literais completos (`hidden sm:grid`/`sm:hidden` vs
   * `hidden min-[900px]:grid`/`min-[900px]:hidden`) em vez de interpolar um
   * fragmento do breakpoint.
   */
  mobileBreakpoint?: 'sm' | 900;
  /**
   * Cadeia de colapso progressivo (@container em index.css) aplicada à toolbar —
   * hoje cobre busca/avatares/métricas/seletor de funil e o padding do CTA
   * primário. 'ds-tb' = CRM/app.html · 'hd-tb' = Help Desk · 'at-tb' =
   * Atendimentos · 'cl-tb' = listas CRM. As LEGENDAS dos controles não-primários
   * NÃO passam mais pelas cadeias: usam o padrão Tailwind do DS
   * (`hidden 2xl:@tb:inline` — viewport ≥2xl E toolbar ≥68rem), ancorado no
   * `@container` que esta casca aplica.
   */
  chain?: 'ds-tb' | 'hd-tb' | 'at-tb' | 'cl-tb';
  'data-testid'?: string;
}

export const PageToolbar: React.FC<PageToolbarProps> = ({
  left,
  center,
  right,
  below,
  mobile,
  mobileBreakpoint = 'sm',
  chain = 'ds-tb',
  'data-testid': dataTestId,
}) => {
  const mobileGridRow = (
    <>
      <div className="flex items-center gap-2.5 min-w-0">{left}</div>
      {center !== undefined && <div className="flex justify-center min-w-0">{center}</div>}
      <div className="flex items-center gap-2 justify-end">{right}</div>
    </>
  );
  const mobileGridStyle: React.CSSProperties = {
    gridTemplateColumns: center ? 'auto 1fr auto' : 'auto auto',
    justifyContent: center ? undefined : 'space-between',
    minHeight: HEADER_HEIGHT,
    backgroundColor: 'var(--theme-header-background)',
    borderBottom: '1px solid var(--theme-header-border)',
  };

  return (
    <div className="flex-none" data-testid={dataTestId}>
      {mobile ? (
        mobileBreakpoint === 900 ? (
          <div className={`${chain} @container hidden min-[900px]:grid items-center gap-3 px-4`} style={mobileGridStyle}>
            {mobileGridRow}
          </div>
        ) : (
          <div className={`${chain} @container hidden sm:grid items-center gap-3 px-4`} style={mobileGridStyle}>
            {mobileGridRow}
          </div>
        )
      ) : (
        /* Sem linha mobile própria: responsivo padrão — <lg a busca desce pra 2ª
           linha em largura total (nunca "desktop espremido"). */
        <div
          className={`${chain} @container grid items-center gap-x-3 gap-y-2 px-4 py-2 min-[900px]:py-0 grid-cols-[auto_1fr] min-[900px]:grid-cols-[auto_1fr_auto]`}
          style={{
            minHeight: HEADER_HEIGHT,
            backgroundColor: 'var(--theme-header-background)',
            borderBottom: '1px solid var(--theme-header-border)',
          }}
        >
          <div className="flex items-center gap-2.5 min-w-0">{left}</div>
          {center !== undefined && (
            <div className="flex justify-center min-w-0 col-span-2 order-last min-[900px]:col-span-1 min-[900px]:order-none">
              {center}
            </div>
          )}
          <div className="flex items-center gap-2 justify-end">{right}</div>
        </div>
      )}
      {mobile && (
        mobileBreakpoint === 900 ? (
          <div
            className="min-[900px]:hidden flex items-center gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            style={{
              padding: '9px 14px',
              backgroundColor: 'var(--theme-header-background)',
              borderBottom: '1px solid var(--theme-header-border)',
            }}
          >
            {mobile}
          </div>
        ) : (
          <div
            className="sm:hidden flex items-center gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            style={{
              padding: '9px 14px',
              backgroundColor: 'var(--theme-header-background)',
              borderBottom: '1px solid var(--theme-header-border)',
            }}
          >
            {mobile}
          </div>
        )
      )}
      {below}
    </div>
  );
};

PageToolbar.displayName = 'PageToolbar';
